import { useContext, useEffect, useRef, useState } from 'react';

import { RealtimeContext } from './realtimeContext';
import type { PresenceHandlers, PublicationHandler, RealtimeStatus } from './types';

/** Connection state. `disabled` when `VITE_CENTRIFUGO_URL` is unset. */
export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext).status;
}

/** The caller's own channel in the active org; null until a socket is granted. */
export function useRealtimeUserChannel(): string | null {
  return useContext(RealtimeContext).userChannel;
}

/**
 * Watch one channel for as long as the component is mounted, and return
 * whether it is live (subscribed and delivering) — use that to relax polling,
 * never to skip it. `channel` may be null while it is unknown — nothing is
 * registered until it resolves. The handlers are read through refs so a new
 * closure per render doesn't churn the subscription. `presence` hears a
 * presence channel's join/leave, when the server sends this member any.
 */
export function useRealtimeChannel<T>(
  channel: string | null | undefined,
  handler: PublicationHandler<T>,
  presence?: PresenceHandlers,
): boolean {
  const { subscribe } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  const presenceRef = useRef(presence);
  const [live, setLive] = useState(false);

  useEffect(() => {
    handlerRef.current = handler;
    presenceRef.current = presence;
  }, [handler, presence]);

  useEffect(() => {
    if (!channel) return;
    const unsubscribe = subscribe<T>(channel, (data) => handlerRef.current(data), setLive, {
      onJoin: (user) => presenceRef.current?.onJoin?.(user),
      onLeave: (user) => presenceRef.current?.onLeave?.(user),
    });
    return () => {
      unsubscribe();
      setLive(false);
    };
  }, [channel, subscribe]);

  return channel ? live : false;
}

const ignorePublication = () => {};

/**
 * Join the org's presence channel while mounted — being subscribed is what
 * makes the caller count as online (backend ADR-107). `handlers` hear
 * join/leave, which the server only sends to staff.
 */
export function useRealtimePresence(handlers?: PresenceHandlers): boolean {
  const { presenceChannel } = useContext(RealtimeContext);
  return useRealtimeChannel(presenceChannel, ignorePublication, handlers);
}
